import React, { useContext, useState, useEffect, useMemo } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, Pane } from "react-leaflet";

import "leaflet/dist/leaflet.css";
import { v4 as uuid } from "uuid";
import Proj from "proj4leaflet";

import { FormContext } from "../context/FormContext";
import { ToolContext } from "../context/ToolContext";
import { useSelector } from "react-redux";
import Sidebar from "./Sidebar";
import ControlTabs from "./ControlTabs";
import MapControls from "./MapControls";
import Navbar from "./Navbar";
import AlertModal from "./AlertModal";
import { isEmpty } from "../utils/isEmpty";

import GeoJsonLoader from "./GeoJsonLoader";
import GeometryLoader from "./GeometryLoader";
import Spinner from "./Spinner";
import * as layout from "../constants/layout";
import { isKeyboardShortcut } from "../utils/isKeyboardShortcut";
import useUserInfoAndLogging from "../utils/useUserInfoAndLogging";

import { ZIndex } from "../constants/zindex";

const crs = new Proj.CRS(
  "EPSG:27700",
  "+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs",
  {
    resolutions: [
      896.0, 448.0, 224.0, 112.0, 56.0, 28.0, 14.0, 7.0, 3.5, 1.75, 0.875, 0.4375, 0.21875,
      0.109375, 0.0546875, 0.02734375, 0.013671875, 0.0068359375, 0.00341796875,
    ],
    origin: [-238375.0, 1376256.0],
  },
);

const ConnectLV = () => {
  const [id, setId] = useState();
  const { formState } = useContext(FormContext);
  const { toolState, setToolState } = useContext(ToolContext);
  const { clientSettings, reference } = formState;
  const [navbarHeight, setNavbarHeight] = useState(layout.NAVBAR_HEIGHT);
  const [keyboardShortcut, setKeyboardShortcut] = useState(false);
  const showTiles = useSelector((state) => state.settings.showMapBackground);
  const geoJson = useSelector((state) => state.settings.geoJson);
  const idnoBoundaries = useSelector((state) => state.settings.isIDNOBoundariesEnabled);
  const hideMap = useSelector((state) => state.network.present.hideMap);

  const {
    defaultMapCenterLat,
    defaultMapCenterLng,
    defaultZoomLevel,
    minZoomLevel,
    maxZoomLevel,
    maxNativeZoomLevel,
    projection,
  } = clientSettings.map;

  const { HistoryModeEnabled, MapTilesEnabled, SideBarEnabled } = clientSettings.features;
  const crsProp = {};

  if (projection === "EPSG27700") {
    crsProp.crs = crs;
  }

  const { logMetric } = useUserInfoAndLogging();
  const setCurrentMap = (currentMap) => {
    if (currentMap && toolState.leafletMap !== currentMap) {
      setToolState({ leafletMap: currentMap });
    }
  };

  const _onMoveEnd = (e) => {
    setId(uuid());
  };

  const { isLoading } = toolState;

  const handleWindowSizeChange = () => {
    const navbar = document.getElementById("ea_navbar");
    setNavbarHeight(navbar.clientHeight);
  };

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  useEffect(() => {
    logMetric("User", "initialising");
  }, []);

  const onKeyDown = (e) => {
    if (isKeyboardShortcut(e, HistoryModeEnabled)) {
      setKeyboardShortcut(true);
    } else {
      return;
    }
    const { ctrlKey, shiftKey, key } = e;
    const _toolState = toolState;
    _toolState.keyDown = { ctrlKey, shiftKey, key };
    setToolState(_toolState);
  };

  const onKeyUp = () => {
    if (keyboardShortcut) {
      setKeyboardShortcut(false);
    } else {
      return;
    }
    const _toolState = toolState;
    _toolState.keyDown = {};
    setToolState(_toolState);
  };

  const canvasRenderer = useMemo(
    () =>
      L.canvas({
        tolerance: parseInt(clientSettings.general.easyClickTolerance),
      }),
    [],
  );

  const svgRenderer = useMemo(() => L.svg({ pane: "svg-pane" }), []);

  return (
    <div
      id="wrapper"
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      tabIndex="-1"
      style={{ width: "100%", height: "100%" }}
    >
      <Navbar />
      {reference && (
        <>
          {SideBarEnabled && (
            <Sidebar position={navbarHeight}>
              <ControlTabs />
            </Sidebar>
          )}
        </>
      )}
      {clientSettings.map && (
        <MapContainer
          id="map"
          center={[defaultMapCenterLat, defaultMapCenterLng]}
          zoom={defaultZoomLevel}
          doubleClickZoom={false}
          zoomControl={false}
          attributionControl={false}
          minZoom={minZoomLevel}
          maxZoom={maxZoomLevel}
          onMoveEnd={_onMoveEnd}
          ref={(mapInstance) => setCurrentMap(mapInstance)}
          renderer={canvasRenderer}
          {...crsProp}
        >
          <Pane name="svg-pane" style={{ zIndex: ZIndex.connectlvSVG }}></Pane>
          <Pane
            name="siteplan-polygon-pane"
            style={{ zIndex: ZIndex.connectlvSiteplanPolygon }}
          ></Pane>

          <Pane name="tile-layer" style={{ zIndex: ZIndex.connectlvTileLayer }}>
            {MapTilesEnabled && showTiles && !hideMap && (
              <TileLayer
                url={clientSettings.urls.endPoints.mapTiles}
                maxNativeZoom={parseInt(maxNativeZoomLevel)}
                maxZoom={24}
              />
            )}
            {[clientSettings.general.geoServerLayers]
              .filter((f) => geoJson.includes(f.name))
              .map((layer) => (
                <GeoJsonLoader id={id} layer={layer} />
              ))}
            {idnoBoundaries && clientSettings.features.LayerIDNOBoundaryEnabled && (
              <GeometryLoader />
            )}
          </Pane>
          <MapControls renderer={canvasRenderer} fallbackRenderer={svgRenderer} />
        </MapContainer>
      )}
      {!isEmpty(toolState.alert) && <AlertModal />}
      {isLoading && <Spinner>{isLoading}</Spinner>}
    </div>
  );
};

export default ConnectLV;
